import { Link } from "react-router-dom";
import { ArrowRight, Code2, Globe, ShoppingCart, BarChart3, Cpu, Shield, Zap, Users } from "lucide-react";
import Spotlight from "../components/Spotlight";
import SectionTransition from "../components/SectionTransition";

const services = [
  {
    icon: Code2,
    title: "Software Development",
    desc: "Custom-built enterprise solutions tailored to your business needs.",
  },
  {
    icon: Globe,
    title: "Web Development",
    desc: "High-performance web applications with modern frameworks.",
  },
  {
    icon: ShoppingCart,
    title: "E-Commerce Development",
    desc: "Scalable online stores that drive revenue and growth.",
  },
  {
    icon: BarChart3,
    title: "Digital Marketing",
    desc: "Data-driven strategies to amplify your digital presence.",
  },
];

const products = [
  { name: "Custom CRM", desc: "Streamline customer relationships", icon: Users },
  { name: "AI Analytics", desc: "Intelligent business insights", icon: Cpu },
  { name: "Enterprise SaaS", desc: "Cloud-native platforms", icon: Shield },
  { name: "E-Commerce Suite", desc: "End-to-end retail solutions", icon: ShoppingCart },
  { name: "ERP Solutions", desc: "Unified business operations", icon: Zap },
  { name: "POS Systems", desc: "Next-gen point of sale", icon: BarChart3 },
];

const stats = [
  { value: "53+", label: "Projects Delivered" },
  { value: "6+", label: "Trusted Clients" },
  { value: "420+", label: "Trained Students"},
  { value: "1+", label: "Products Built" },
];

export default function HomePage() {
  return (
    <div data-testid="home-page">
      {/* ===== HERO SECTION ===== */}
      <Spotlight className="relative min-h-screen bg-neutral-950 grid-pattern overflow-hidden">
        {/* Ambient orbs */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/4 w-72 h-72 bg-yellow-500/5 rounded-full blur-[100px] pointer-events-none" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pt-32 pb-20 md:pt-48 md:pb-32">
          <div className="max-w-4xl mx-auto text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/10 bg-white/5 text-sm text-slate-400 mb-8 animate-fade-in-up">
              <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
              Product &amp; Service Based Company
            </div>

            {/* Main Heading */}
            <h1
              data-testid="hero-heading"
              className="text-5xl md:text-7xl font-bold tracking-tight leading-[1.1] text-white mb-8 animate-fade-in-up animation-delay-200"
            >
              Building the Future
              <span className="block mt-2">
                of <span className="text-blue-500">Tech</span><span className="text-yellow-500">nology</span>
              </span>
            </h1>

            {/* Subtext */}
            <p className="text-lg md:text-xl text-slate-400 max-w-2xl mx-auto mb-12 leading-relaxed animate-fade-in-up animation-delay-400">
              We engineer world-class software products and deliver transformative digital 
              services. From concept to deployment, we turn ambitious ideas into reality.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in-up animation-delay-600">
              <Link
                to="/services"
                data-testid="hero-explore-btn"
                className="px-8 py-3.5 rounded-full bg-white text-black font-semibold text-sm hover:bg-slate-200 transition-all duration-200 active:scale-95 flex items-center gap-2"
              >
                Explore Services <ArrowRight size={16} />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-contact-btn"
                className="px-8 py-3.5 rounded-full border border-white/20 text-white font-medium text-sm hover:bg-white/10 transition-all duration-200"
              >
                Get in Touch
              </Link>
            </div>
          </div>

          {/* Stats Row */}
          <div className="mt-24 grid grid-cols-2 md:grid-cols-4 gap-8 max-w-3xl mx-auto">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-sm text-slate-500">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom fade */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent" />
      </Spotlight>

      {/* ===== SERVICES PREVIEW ===== */}
      <section className="bg-white py-24 md:py-32" data-testid="home-services-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionTransition>
            <p className="text-sm font-medium tracking-wider uppercase text-blue-600 mb-4">What We Do</p>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-slate-900 mb-6">
              Services Built for Growth
            </h2>
            <p className="text-base md:text-lg text-slate-600 max-w-2xl mb-16">
              We offer comprehensive technology solutions that help businesses scale, 
              innovate, and stay ahead in a competitive landscape.
            </p>
          </SectionTransition>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((service, i) => (
              <SectionTransition key={service.title} delay={i * 0.1}>
                <div
                  data-testid={`home-service-card-${i}`}
                  className="group p-8 rounded-2xl border border-slate-200 hover:border-blue-200 bg-white hover:bg-slate-50/50 transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center mb-6 group-hover:bg-blue-50 transition-colors">
                    <service.icon size={24} className="text-slate-700 group-hover:text-blue-600 transition-colors" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">{service.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{service.desc}</p>
                </div>
              </SectionTransition>
            ))}
          </div>

          <SectionTransition delay={0.4}>
            <div className="mt-12 text-center">
              <Link
                to="/services"
                data-testid="home-view-services-btn"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-slate-900 text-white font-semibold text-sm hover:bg-slate-800 transition-all duration-200 active:scale-95"
              >
                View All Services <ArrowRight size={16} />
              </Link>
            </div>
          </SectionTransition>
        </div>
      </section>

      {/* ===== PRODUCTS PREVIEW ===== */}
      {/* <section className="bg-slate-50 py-24 md:py-32" data-testid="home-products-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionTransition>
            <p className="text-sm font-medium tracking-wider uppercase text-blue-600 mb-4">Our Products</p>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-slate-900 mb-6">
              Purpose-Built Solutions
            </h2>
            <p className="text-base md:text-lg text-slate-600 max-w-2xl mb-16">
              Ready-to-deploy products designed for modern businesses, crafted with 
              cutting-edge technology and enterprise-grade reliability.
            </p>
          </SectionTransition>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {products.map((product, i) => (
              <SectionTransition key={product.name} delay={i * 0.08}>
                <div
                  data-testid={`home-product-card-${i}`}
                  className="group relative p-8 rounded-2xl bg-white border border-slate-200 hover:border-slate-300 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                >
                  <div className="w-11 h-11 rounded-lg bg-blue-50 flex items-center justify-center mb-5">
                    <product.icon size={22} className="text-blue-600" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{product.name}</h3>
                  <p className="text-slate-500 text-sm">{product.desc}</p>
                </div>
              </SectionTransition>
            ))}
          </div>

          <SectionTransition delay={0.5}>
            <div className="mt-12 text-center">
              <Link
                to="/products"
                data-testid="home-view-products-btn"
                className="inline-flex items-center gap-2 text-blue-600 font-semibold text-sm hover:text-blue-800 transition-colors"
              >
                Explore All Products <ArrowRight size={16} />
              </Link>
            </div>
          </SectionTransition>
        </div>
      </section> */}

      {/* ===== INTERNSHIP TEASER ===== */}
      <section className="bg-neutral-950 py-24 md:py-32 relative overflow-hidden" data-testid="home-internship-section">
        <div className="absolute inset-0 grid-pattern opacity-50" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/5 rounded-full blur-[120px]" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <SectionTransition>
            <div className="max-w-3xl">
              <p className="text-sm font-medium tracking-wider uppercase text-blue-400 mb-4">Internship Program</p>
              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-white mb-6">
                Launch Your Tech Career
              </h2>
              <p className="text-base md:text-lg text-slate-400 mb-10 leading-relaxed">
                Join our internship program and gain hands-on experience with real-world projects.
                Earn industry-recognized certificates in 15+ technology domains.
              </p>
              <div className="flex flex-wrap gap-3 mb-10">
                {["React", "Python", "Java Spring", "MERN", "AI / ML", "Blockchain", "Gen AI"].map((tag) => (
                  <span
                    key={tag}
                    className="px-4 py-1.5 rounded-full border border-white/10 bg-white/5 text-sm text-slate-300"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <Link
                to="/internships"
                data-testid="home-internships-btn"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-white text-black font-semibold text-sm hover:bg-slate-200 transition-all duration-200 active:scale-95"
              >
                Learn More <ArrowRight size={16} />
              </Link>
            </div>
          </SectionTransition>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="bg-white py-24 md:py-32" data-testid="home-cta-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <SectionTransition>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-slate-900 mb-6">
              Ready to Build Something Great?
            </h2>
            <p className="text-base md:text-lg text-slate-600 max-w-xl mx-auto mb-10">
              Let us help you bring your vision to life. From innovative products to 
              scalable services, we have the expertise to deliver.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/contact"
                data-testid="home-final-cta-btn"
                className="px-8 py-3.5 rounded-full bg-slate-900 text-white font-semibold text-sm hover:bg-slate-800 transition-all duration-200 active:scale-95 flex items-center gap-2"
              >
                Start a Project <ArrowRight size={16} />
              </Link>
              <Link
                to="/about"
                className="px-8 py-3.5 rounded-full border border-slate-300 text-slate-700 font-medium text-sm hover:bg-slate-50 transition-all duration-200"
              >
                Learn About Us
              </Link>
            </div>
          </SectionTransition>
        </div>
      </section>
    </div>
  );
}
